import { createTheme } from '@material-ui/core/styles';
import createBreakpoints from '@material-ui/core/styles/createBreakpoints';

const breakpoints = createBreakpoints({});

// Rebrand color constants

const CONTRAST_TEAL = '#13352C';

const WHITE = '#FFFFFF';
const BLACK = '#000000';

const GRAY_2 = '#DFE1DF';

// Button colors
const BUTTON_PRIMARY_BG = '#13352C';
const BUTTON_PRIMARY_TEXT = '#FFFFFF';
const BUTTON_SECONDARY_BG = '#78CFB8';
const BUTTON_SECONDARY_TEXT = '#0B1E19';

const theme = createTheme({
  palette: {
    primary: {
      main: '#30C67C',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#40C9FF',
      contrastText: '#ffffff',
    },
    grey: {
      dark: '#293240',
      main: '#B1B1B1',
      border: '#E5E8ED',
      light: '#97A3B4',
      background: '#F9F9F9',
      overlay: '#293240',
      hover: '#E5E8ED',
      textPrimary: '#222721',
    },
    success: {
      // main: '#8FDDC2',
      main: '#2DB77E',
    },
    error: {
      main: '#D45163',
    },
    warning: {
      main: '#F9D952',
    },
    info: {
      main: '#40C9FF',
    },
    extra: {
      lightGrey: '#E7E7E7',
      grey: '#D8D8D8',
      pink: '#F57A9D',
      brightPink: '#F94BA6',
      yellow: '#FED924',
      gold: '#F5B529',
      orange: '#FE953D',
      limeGreen: '#2EE88B',
      forestGreen: '#36AD8B',
      darkGreen: '#3E5F61',
      purple: '#A046E4',
      royalBlue: '#425CFF',
      darkBlue: '#233294',
      system: '#C9E7E5',
      white: '#fff',
      frozen: '#3865f4',
    },
    text: {
      primary: '#222721',
      secondary: '#000000',
      disabled: '#333333',
      hint: '#222721',
    },
    background: {
      default: '#fff',
    },
    action: {
      disabledBackground: '#B1B1B1',
      disabled: '#999999',
    },
  },
  // Set all shadows to 'none' to remove box-shadows from buttons and other MUI components
  // MUI v4 uses this array for elevation-based shadows (e.g., contained buttons use shadows[2])
  shadows: Array(25).fill('none'),
  typography: {
    fontFamily: "'DM Sans', 'proxima-nova', sans-serif",
    fontSize: 16,
  },
  overrides: {
    MuiTypography: {
      root: {
        fontSize: '16px',
      },
      h1: {
        fontSize: '20px',
        fontFamily: "'DM Sans', 'avant-garde', sans-serif",
        fontWeight: 600,
      },
      h2: {
        fontSize: '18px',
        fontFamily: "'DM Sans', 'avant-garde', sans-serif",
        fontWeight: 600,
      },
      h3: {
        fontSize: '16px',
        fontFamily: "'DM Sans', 'avant-garde', sans-serif",
        fontWeight: 600,
      },
      h4: {
        fontFamily: "'DM Sans', 'avant-garde', sans-serif",
        fontWeight: 600,
      },
      h5: {
        fontFamily: "'DM Sans', 'avant-garde', sans-serif",
        fontWeight: 600,
      },
      h6: {
        fontFamily: "'DM Sans', 'avant-garde', sans-serif",
        fontWeight: 600,
      },
      body1: {
        fontSize: '16px',
      },
      body2: {
        fontSize: '18px',
      },
      // subtitle1: {
      //     fontSize: '17px',
      //     fontWeight: 600
      // },
      // subtitle2: {
      //     fontSize: '16px',
      //     fontWeight: '400'
      // },
      overline: {
        fontSize: '16px',
      },
    },
    MuiBadge: {
      badge: {
        backgroundColor: '#2D2D2D',
        color: '#EFF3F6',
      },
    },
    MuiListItem: {
      gutters: {
        paddingLeft: 8,
        paddingRight: 8,
      },
    },
    MuiListItemText: {
      root: {
        paddingLeft: 8,
        paddingRight: 8,
      },
    },
    MuiTableCell: {
      root: {
        padding: '4px 10px',
      },
      body: {
        fontSize: '.75rem',
      },
      head: {
        fontSize: '.80rem',
        fontWeight: 500,
      },
    },
    MuiFormControl: {
      marginNormal: {
        marginTop: '6px',
        marginBottom: '8px',
      },
    },
    MuiCheckbox: {
      root: {
        padding: 6,
        paddingRight: 4,
        color: 'rgba(0, 0, 0, 0.65)',
        '&:hover': {
          color: CONTRAST_TEAL,
          backgroundColor: 'transparent',
        },
        '&.Mui-checked': {
          color: CONTRAST_TEAL,
          '&:hover': {
            backgroundColor: 'transparent',
          },
        },
      },
    },
    MuiButtonBase: {
      root: {
        [breakpoints.up('sm')]: {
          whiteSpace: 'nowrap',
        },
        boxShadow: 'none',
        '&:hover': {
          boxShadow: 'none',
        },
        '&:focus': {
          boxShadow: 'none',
        },
      },
    },
    // Button styles ported from Web Portal rebrandTheme
    // Note: MUI v4 doesn't support color="destructive". To use destructive styling,
    // add className="button-destructive" to your Button component.
    MuiButton: {
      root: {
        boxShadow: 'none',
        minWidth: '100px',
        borderRadius: '50px',
        fontWeight: 600,
        textTransform: 'none',
        lineHeight: '0px',
        padding: '6px 16px',
        height: '48px', // Default to large size (48px) to match Web Portal
        '&:hover': {
          boxShadow: 'none',
        },
        '&:focus, &:active': {
          boxShadow: 'none',
        },
        // Support legacy button-action class
        '&.button-action': {
          backgroundColor: BUTTON_PRIMARY_BG,
          color: BUTTON_PRIMARY_TEXT,
          height: '36px',
          '&:hover': {
            backgroundColor: '#112F26', // 10% darker
            boxShadow: 'none',
          },
          '&:focus, &:active': {
            backgroundColor: '#0E2820', // 20% darker
            boxShadow: 'none',
          },
        },
        // Destructive button variant (use className="button-destructive")
        '&.button-destructive': {
          backgroundColor: '#d45163',
          color: WHITE,
          border: 'none',
          '& .MuiButton-startIcon': {
            color: WHITE,
            '& svg': {
              fill: WHITE,
            },
            '& svg path': {
              fill: WHITE,
            },
          },
          '& .MuiButton-endIcon': {
            color: WHITE,
            '& svg': {
              fill: WHITE,
            },
            '& svg path': {
              fill: WHITE,
            },
          },
          '&:hover': {
            backgroundColor: '#b02c4d',
            boxShadow: 'none',
          },
          '&:focus, &:active': {
            backgroundColor: '#850715',
            boxShadow: 'none',
          },
        },
      },
      // Size variants
      sizeSmall: {
        height: '32px',
      },
      sizeMedium: {
        height: '36px',
      },
      sizeLarge: {
        height: '48px',
      },
      // Text variant
      text: {
        backgroundColor: 'transparent',
        color: BUTTON_PRIMARY_BG,
        border: 'none',
        '& .MuiButton-startIcon': {
          color: BUTTON_PRIMARY_BG,
          '& svg': {
            fill: BUTTON_PRIMARY_BG,
          },
          '& svg path': {
            fill: BUTTON_PRIMARY_BG,
          },
        },
        '& .MuiButton-endIcon': {
          color: BUTTON_PRIMARY_BG,
          '& svg': {
            fill: BUTTON_PRIMARY_BG,
          },
          '& svg path': {
            fill: BUTTON_PRIMARY_BG,
          },
        },
        '&:hover': {
          backgroundColor: 'rgba(120, 207, 184, 0.2)', // 20% BUTTON_SECONDARY_BG
          boxShadow: 'none',
        },
        '&:focus, &:active': {
          backgroundColor: 'rgba(120, 207, 184, 0.3)', // 30% BUTTON_SECONDARY_BG
          boxShadow: 'none',
        },
      },
      // Contained variant (default primary styling)
      contained: {
        backgroundColor: BUTTON_PRIMARY_BG,
        color: BUTTON_PRIMARY_TEXT,
        '& .MuiButton-startIcon': {
          color: BUTTON_PRIMARY_TEXT,
          '& svg': {
            fill: BUTTON_PRIMARY_TEXT,
          },
          '& svg path': {
            fill: BUTTON_PRIMARY_TEXT,
          },
        },
        '& .MuiButton-endIcon': {
          color: BUTTON_PRIMARY_TEXT,
          '& svg': {
            fill: BUTTON_PRIMARY_TEXT,
          },
          '& svg path': {
            fill: BUTTON_PRIMARY_TEXT,
          },
        },
        '&:hover': {
          backgroundColor: '#112F26', // 10% darker
          boxShadow: 'none',
        },
        '&:focus, &:active': {
          backgroundColor: '#0E2820', // 20% darker
          boxShadow: 'none',
        },
      },
      // Primary variant
      containedPrimary: {
        backgroundColor: BUTTON_PRIMARY_BG,
        color: BUTTON_PRIMARY_TEXT,
        '& .MuiButton-startIcon': {
          color: BUTTON_PRIMARY_TEXT,
          '& svg': {
            fill: BUTTON_PRIMARY_TEXT,
          },
          '& svg path': {
            fill: BUTTON_PRIMARY_TEXT,
          },
        },
        '& .MuiButton-endIcon': {
          color: BUTTON_PRIMARY_TEXT,
          '& svg': {
            fill: BUTTON_PRIMARY_TEXT,
          },
          '& svg path': {
            fill: BUTTON_PRIMARY_TEXT,
          },
        },
        '&:hover': {
          backgroundColor: '#112F26', // 10% darker
          boxShadow: 'none',
        },
        '&:focus, &:active': {
          backgroundColor: '#0E2820', // 20% darker
          boxShadow: 'none',
        },
      },
      // Secondary variant
      containedSecondary: {
        backgroundColor: 'rgba(120, 207, 184, 0.2)', // 20% BUTTON_SECONDARY_BG
        color: BUTTON_SECONDARY_TEXT,
        '& .MuiButton-startIcon': {
          color: BUTTON_SECONDARY_TEXT,
          '& svg': {
            fill: BUTTON_SECONDARY_TEXT,
          },
          '& svg path': {
            fill: BUTTON_SECONDARY_TEXT,
          },
        },
        '& .MuiButton-endIcon': {
          color: BUTTON_SECONDARY_TEXT,
          '& svg': {
            fill: BUTTON_SECONDARY_TEXT,
          },
          '& svg path': {
            fill: BUTTON_SECONDARY_TEXT,
          },
        },
        '&:hover': {
          backgroundColor: 'rgba(120, 207, 184, 0.3)', // 30% BUTTON_SECONDARY_BG
          boxShadow: 'none',
        },
        '&:focus, &:active': {
          backgroundColor: 'rgba(120, 207, 184, 0.4)', // 40% BUTTON_SECONDARY_BG
          boxShadow: 'none',
        },
      },
      // Outlined variants
      outlined: {
        backgroundColor: 'transparent',
        border: `1px solid ${BUTTON_PRIMARY_BG}`,
        color: BUTTON_PRIMARY_BG,
        '&:hover': {
          backgroundColor: 'rgba(120, 207, 184, 0.2)',
          border: `1px solid ${BUTTON_PRIMARY_BG}`,
          boxShadow: 'none',
        },
        '&:focus, &:active': {
          backgroundColor: 'rgba(120, 207, 184, 0.3)',
          border: `1px solid ${BUTTON_PRIMARY_BG}`,
          boxShadow: 'none',
        },
      },
      outlinedPrimary: {
        backgroundColor: 'transparent',
        border: `1px solid ${BUTTON_PRIMARY_BG}`,
        color: BUTTON_PRIMARY_BG,
        '&:hover': {
          backgroundColor: 'rgba(120, 207, 184, 0.2)',
          border: `1px solid ${BUTTON_PRIMARY_BG}`,
          boxShadow: 'none',
        },
        '&:focus, &:active': {
          backgroundColor: 'rgba(120, 207, 184, 0.3)',
          border: `1px solid ${BUTTON_PRIMARY_BG}`,
          boxShadow: 'none',
        },
      },
      outlinedSecondary: {
        backgroundColor: 'transparent',
        border: `1px solid ${BUTTON_SECONDARY_BG}`,
        color: BUTTON_SECONDARY_TEXT,
        '&:hover': {
          backgroundColor: 'rgba(120, 207, 184, 0.2)',
          border: `1px solid ${BUTTON_SECONDARY_BG}`,
          boxShadow: 'none',
        },
        '&:focus, &:active': {
          backgroundColor: 'rgba(120, 207, 184, 0.3)',
          border: `1px solid ${BUTTON_SECONDARY_BG}`,
          boxShadow: 'none',
        },
      },
      // Disabled state
      disabled: {
        backgroundColor: `${GRAY_2} !important`,
        color: `${WHITE} !important`,
        cursor: 'not-allowed',
        pointerEvents: 'auto',
        '& .MuiButton-startIcon': {
          color: WHITE,
          '& svg': {
            fill: WHITE,
          },
          '& svg path': {
            fill: WHITE,
          },
        },
        '& .MuiButton-endIcon': {
          color: WHITE,
          '& svg': {
            fill: WHITE,
          },
          '& svg path': {
            fill: WHITE,
          },
        },
      },
      label: {
        fontFamily: "'DM Sans', 'proxima-nova-semi', Inter, Roboto, Arial, sans-serif",
        fontSize: '16px',
        fontWeight: '600',
      },
    },

    MuiIconButton: {
      root: {
        padding: '6px',
      },
    },
    MuiDialogTitle: {
      root: {
        padding: '32px 24px 24px',
        textAlign: 'center',
      },
    },
    MuiDialogContent: {
      root: {
        backgroundColor: '#fff',
        [breakpoints.down('sm')]: {
          overflowY: 'initial',
          minHeight: 'auto !important',
        },
      },
    },
    MuiDialogActions: {
      root: {
        margin: 0,
      },
    },
    MuiFormLabel: {
      root: {
        fontFamily: "'DM Sans', 'proxima-nova-semi', sans-serif",
        fontSize: '12px',
        textTransform: 'lowercase',
        transform: 'none !important',
        position: 'relative !important',
        top: 'auto !important',
        left: 'auto !important',
        borderColor: '#E5E8ED',
        margin: '0',
        padding: '0 8px',
        lineHeight: '16px',
        '&$focused': {
          color: '#222721',
        },
      },
      asterisk: {
        color: 'red',
      },
    },
    MuiFormControlLabel: {
      root: {
        marginLeft: '0',
      },
      label: {
        fontSize: '12px',
      },
    },
    MuiPaper: {
      rounded: {
        borderRadius: '8px',
      },
    },

    MuiBackdrop: {
      root: {
        backgroundColor: 'rgba(0,0,0,.75)',
      },
    },
    MuiDrawer: {
      paper: {},
    },
    MuiCard: {
      root: {
        overflow: 'visible',
      },
    },
    PrivateTabIndicator: {
      root: {
        height: '4px',
        borderRadius: '2px',
      },
    },
    MuiAutocomplete: {
      inputRoot: {
        '&&[class*="MuiOutlinedInput-root"]': {
          padding: 0,
        },
      },
    },
    MuiInputBase: {
      root: {
        overflow: 'hidden',
        color: '#222721',
        '&$input': {
          borderRadius: '12px',
          transition: 'all 0.25s ease',
          border: '1px #E5E8ED solid',
          padding: '14px',
        },
      },
    },
    MuiOutlinedInput: {
      root: {
        borderRadius: '12px',
        border: '1px #E5E8ED solid',
      },
      input: {
        padding: '14px',
      },
    },
    MuiTabs: {
      root: {},
      indicator: {},
    },
    MuiTab: {
      root: {
        // minWidth: 'initial !important',

        '&:last-child': {
          borderRight: 'none',
        },
      },
    },
    MuiAppBar: {
      colorDefault: {
        backgroundColor: '#F9F9F9',
      },
    },
    MuiTablePagination: {
      root: {
        color: '#222721',
      },
    },
  },
});

export default theme;
